import { InInUser, UserContext } from 'components/UserContext';
import { useCallback, useContext } from 'react';
import { doc, updateDoc } from "firebase/firestore";
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useSignOut } from 'components/User';
import { usePageTitle } from 'routes/Util/Util.hooks';
import { Field, Form, Formik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { firestore } from 'fb';
import { ProfilePhotoField } from 'components/Profile/PhotoField/ProfilePhotoField';
import { useUploadMutation } from 'components/Profile/PhotoField/ProfilePhotoField.hooks';
import { toast } from 'react-toastify';

export const useProfileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(async ({ user, values }: { user: InInUser, values: Partial<InInUser> }) => {
    const ref = doc(firestore, "users", user.uid);
    await updateDoc(ref, values);
  }, {
    onSuccess: () => {
      queryClient.invalidateQueries('me');
    }
  })
}

export const ProfilePage = () => {
  usePageTitle('프로필');
  const { isLoading, user } = useContext(UserContext)
  const signOut = useSignOut();
  const { mutateAsync: updateProfile } = useProfileMutation();
  const { mutateAsync: uploadPhoto } = useUploadMutation();
  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      if (!user) {
        throw new Error('Cannot update profile without user');
      }

      const nextValues = {
        displayName: values.displayName,
      } as Partial<InInUser>;

      if (values.photoFile) {
        nextValues.photoURL = await uploadPhoto({ user, file: values.photoFile });
      }

      // Whitelist the fields we want to update
      await updateProfile({ user, values: nextValues });
      toast.success('프로필이 업데이트 되었습니다.');
      resetForm();
    }, [updateProfile, uploadPhoto, user]);

  return (
    <Container>
      { isLoading || !user ? (
        <Row><Col>데이터 로딩중...</Col></Row>
      ) : (
        <Formik initialValues={user} onSubmit={onSubmit}>
          {({ isSubmitting }) => (
            <Form>
              <fieldset disabled={isSubmitting}>
                <Row className="mb-3 mt-5">
                  <Col>
                    <ProfilePhotoField user={user} />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col className="d-flex justify-content-end">
                    <Button className="px-0" variant="link" onClick={signOut}>로그아웃</Button>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <label htmlFor="email" className="col-sm-2 col-form-label">이메일</label>
                  <Col sm={10}>
                    <Field id="email" className="form-control" name="email" readOnly />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <label htmlFor="displayName" className="col-sm-2 col-form-label">닉네임</label>
                  <Col sm={10}>
                    <Field id="displayName" className="form-control" name="displayName" />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <label htmlFor="creationTime" className="col-sm-2 col-form-label">가입일</label>
                  <Col sm={10}>
                    <Field id="creationTime" className="form-control" name="creationTime" readOnly value={user.creationTime && new Date(user.creationTime).toLocaleString()} />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <label htmlFor="canReadArticles" className="col-sm-2 col-form-label">커뮤니티 읽기 권한</label>
                  <Col sm={10}>
                    <Field id="canReadArticles" className="form-control" name="canReadArticles" readOnly value={user.canReadArticles ? '가능' : '불가능'} />
                    {!user.canReadArticles && user.canReadReason && <small className="text-warning">{user.canReadReason}</small>}
                  </Col>
                </Row>
                <Row className="mb-3">
                  <label htmlFor="opinionLikes" className="col-sm-2 col-form-label">좋아요 한 의견 수</label>
                  <Col sm={10}>
                    <Field id="opinionLikes" className="form-control" name="opinionLikes" readOnly value={`${user.opinionLikes.length}개`} />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <label htmlFor="opinionReports" className="col-sm-2 col-form-label">신고 한 의견 수</label>
                  <Col sm={10}>
                    <Field id="opinionReports" className="form-control" name="opinionReports" readOnly value={`${user.opinionReports.length}개`} />
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col className="d-flex justify-content-end">
                    <button type="reset" className="btn btn-outline-secondary" disabled={isSubmitting}>
                      초기화
                    </button>
                    <button type="submit" className="btn btn-outline-primary ms-2" disabled={isSubmitting}>
                      수정
                    </button>
                  </Col>
                </Row>
              </fieldset>
            </Form>
          )}
        </Formik>
      )}
    </Container>
  );
};
